import { useEffect, useState, useRef, useMemo } from "react";
import { myGeolocalisedServices } from "../../services";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import maplibregl from "maplibre-gl";
import MaplibreGeocoder from "@maplibre/maplibre-gl-geocoder";
import "@maplibre/maplibre-gl-geocoder/dist/maplibre-gl-geocoder.css";
import DrawerMenu from "./DrawerMenu";

// Import constants and utilities
import { MAP_CONSTANTS, Geo } from "./constants/mapConstants";
import {
  createMarkerElement,
  updateCircle,
  removeCircle,
  addCustomIconToGeocoder,
} from "./utils/mapUtils";

// Import styled components
import { MapContainer } from "./styles/mapStyles";

// Import components
import LocationDetailsCard from "./components/LocationDetailsCard";
import ConfirmDialog from "./components/ConfirmDialog";

const ComponentsMapManagement = ({
  myComponents,
  geolocalisedComponents,
  objectIdToDeleteFromMap,
  setObjectIdToDeleteFromMap,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedThemeComponent, setSelectedThemeComponent] = useState("");
  const [selectedObjectComponent, setSelectedObjectComponent] = useState("");

  const [openDrawerMenu, setOpenDrawerMenu] = useState(false);
  const [selectedRadius, setSelectedRadius] = useState(
    MAP_CONSTANTS.DEFAULT_RADIUS
  );
  const [originalRadius, setOriginalRadius] = useState(
    MAP_CONSTANTS.DEFAULT_RADIUS
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState("");
  const [selectionType, setSelectionType] = useState("themes");

  const mapRef = useRef(null);
  const currentMarkerRef = useRef(null);
  const currentCircleRef = useRef(null);
  const markersRef = useRef([]);
  const circlesRef = useRef([]);

  useEffect(() => {
    if (objectIdToDeleteFromMap) {
      console.log(
        "Deleting markers with component ID:",
        objectIdToDeleteFromMap
      );

      // Find all indices that match the component ID to delete
      const indicesToDelete = [];
      selectedLocations.forEach((location, index) => {
        if (location.component === objectIdToDeleteFromMap) {
          indicesToDelete.push(index);
        }
      });

      // Process in reverse order to avoid index shifting issues
      indicesToDelete
        .sort((a, b) => b - a)
        .forEach((indexToDelete) => {
          // Remove marker from map
          if (markersRef.current[indexToDelete]) {
            markersRef.current[indexToDelete].remove();
          }

          // Remove circle if it exists
          if (circlesRef.current[indexToDelete]) {
            removeCircle(mapRef.current, circlesRef.current[indexToDelete]);
          }
        });

      // Update state and refs without the deleted markers
      if (indicesToDelete.length > 0) {
        // Filter out the deleted locations
        const newLocations = selectedLocations.filter(
          (_, index) => !indicesToDelete.includes(index)
        );
        setSelectedLocations(newLocations);

        // Update the marker refs
        markersRef.current = markersRef.current.filter(
          (_, index) => !indicesToDelete.includes(index)
        );

        // Update the circle refs
        circlesRef.current = circlesRef.current.filter(
          (_, index) => !indicesToDelete.includes(index)
        );

        // Update indices on all remaining markers
        markersRef.current.forEach((marker, idx) => {
          const markerEl = marker.getElement();
          markerEl.dataset.locationIndex = idx.toString();

          // Update the location data with new index
          const locationData = JSON.parse(markerEl.dataset.locationData);
          markerEl.dataset.locationData = JSON.stringify(locationData);
        });

        // If we're currently editing a marker that was deleted, reset the UI
        if (
          isEditMode &&
          selectedIndex !== null &&
          indicesToDelete.includes(selectedIndex)
        ) {
          setSelectedLocation(null);
          setSelectedComponent("");
          setSelectedThemeComponent("");
          setSelectedObjectComponent("");
          setSelectedRadius(MAP_CONSTANTS.DEFAULT_RADIUS);
          setIsEditMode(false);
          setSelectedIndex(null);
        }

        enqueueSnackbar(
          `${indicesToDelete.length} ${t(
            "mapWithComponents.removedLocations"
          )}`,
          {
            variant: "info",
          }
        );
      } else {
        console.log("No matching locations found to delete");
      }

      // Reset to null
      setObjectIdToDeleteFromMap(null);
    }
  }, [
    objectIdToDeleteFromMap,
    setObjectIdToDeleteFromMap,
    selectedLocations,
    enqueueSnackbar,
    isEditMode,
    selectedIndex,
    t,
  ]);

  // geocoder configuration instantiated only once
  const geocoder = useMemo(
    () =>
      new MaplibreGeocoder(Geo, {
        maplibregl,
        showResultsWhileTyping: true,
        marker: false,
        placeholder: t("mapWithComponents.searchPlaceholder"),
        flyTo: true,
        clearOnBlur: false,
        clearAndBlurOnEsc: true,
        collapsed: false,
      }),
    [t]
  );

  // Handle radius change
  const handleRadiusChange = (event) => {
    const value = Math.max(
      MAP_CONSTANTS.MIN_RADIUS,
      Math.min(1000, Number(event.target.value))
    );
    setSelectedRadius(value);

    // Update circle if it exists
    if (currentCircleRef.current && mapRef.current) {
      updateCircle(
        mapRef.current,
        currentCircleRef.current,
        selectedLocation.coordinates,
        value
      );
    }
  };

  // Cancel handler
  const handleCancel = () => {
    if (isEditMode && selectedIndex !== null) {
      // Reset the circle to its original radius
      const circleId = circlesRef.current[selectedIndex];
      if (circleId && mapRef.current) {
        updateCircle(
          mapRef.current,
          circleId,
          selectedLocation.coordinates,
          originalRadius
        );
      }
    } else {
      // Remove circle for new location
      if (currentCircleRef.current) {
        removeCircle(mapRef.current, currentCircleRef.current);
        currentCircleRef.current = null;
      }

      // Remove marker for new location
      if (currentMarkerRef.current) {
        currentMarkerRef.current.remove();
        currentMarkerRef.current = null;
      }
    }

    // Reset state
    setSelectedLocation(null);
    setSelectedComponent("");
    setSelectedThemeComponent(""); // Also reset type-specific selections
    setSelectedObjectComponent(""); // Also reset type-specific selections
    setSelectedRadius(MAP_CONSTANTS.DEFAULT_RADIUS);
    setOriginalRadius(MAP_CONSTANTS.DEFAULT_RADIUS);
    setIsEditMode(false);
    setSelectedIndex(null);
  };

  // Add this function to make the API call
  const saveLocationToAPI = (locationData, isUpdate = false) => {
    console.log(
      `${isUpdate ? "Updating" : "Creating"} location in API:`,
      locationData
    );

    // Format the data for the API
    const apiData = {
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      radius: locationData.radius || 0,
      address: locationData.address,
      [selectionType === "themes" ? "theme" : "model3D"]:
        locationData.component,
    };

    return myGeolocalisedServices
      .createOrUpdateGeolocalisedComponent(apiData, isUpdate, locationData._id)
      .then((response) => {
        enqueueSnackbar(
          isUpdate
            ? t("mapWithComponents.markerUpdatedSuccess")
            : t("mapWithComponents.markerAddedSuccess"),
          { variant: "success" }
        );
        return response;
      })
      .catch((error) => {
        console.error("Error saving location:", error);
        enqueueSnackbar(
          isUpdate
            ? t("mapWithComponents.markerUpdatedFail")
            : t("mapWithComponents.markerAddedFail"),
          {
            variant: "error",
          }
        );
        throw error;
      });
  };

  // Modify handleAccept to call the API
  const handleAccept = () => {
    if (selectedLocation && selectedComponent) {
      // Create location object
      const newLocation = {
        address: selectedLocation.address,
        latitude: selectedLocation.coordinates[1],
        longitude: selectedLocation.coordinates[0],
        component: selectedComponent,
        radius: selectionType === "themes" ? selectedRadius : 0,
        type: selectionType,
      };

      // Save to API first
      saveLocationToAPI(newLocation, false)
        .then((apiResponse) => {
          // Store the API ID in our location object if returned
          if (apiResponse && apiResponse._id) {
            newLocation._id = apiResponse._id;
          }

          // Continue with the existing code to update the UI
          const newLocationIndex = selectedLocations.length;
          setSelectedLocations([...selectedLocations, newLocation]);

          const map = mapRef.current;
          if (map) {
            // IMPORTANT: Remove temporary marker and circle
            if (currentMarkerRef.current) currentMarkerRef.current.remove();
            if (currentCircleRef.current)
              removeCircle(map, currentCircleRef.current);

            // Create custom marker element with image
            const markerElement = createMarkerElement(
              selectedComponent,
              selectionType,
              myComponents
            );

            // Create permanent marker with custom element
            const marker = new maplibregl.Marker({
              element: markerElement,
              draggable: false,
              anchor: "center",
            })
              .setLngLat(selectedLocation.coordinates)
              .addTo(map);

            // Store the complete location data directly in the marker element
            markerElement.dataset.locationIndex = newLocationIndex;
            markerElement.dataset.locationData = JSON.stringify(newLocation);

            // Use proper MapLibre marker click handler for better reliability
            markerElement.onclick = function (e) {
              handleCancel();
              e.stopPropagation();
              try {
                const locationData = JSON.parse(this.dataset.locationData);
                const storedIndex = parseInt(this.dataset.locationIndex, 10);
                console.log(
                  "Marker clicked with data:",
                  locationData,
                  "index:",
                  storedIndex
                );

                handleMarkerClick(locationData, storedIndex);
              } catch (err) {
                console.error("Error accessing location data", err);
              }
            };

            markersRef.current.push(marker);

            // Create permanent circle ONLY for themes
            if (selectionType === "themes") {
              const circleId = `circle-${Date.now()}`;
              updateCircle(
                map,
                circleId,
                selectedLocation.coordinates,
                selectedRadius
              );
              circlesRef.current.push(circleId);
            } else {
              // Push null for objects to maintain index alignment
              circlesRef.current.push(null);
            }

            // Reset references
            currentMarkerRef.current = null;
            currentCircleRef.current = null;
          }

          // Reset states
          setSelectedLocation(null);
          setSelectedComponent("");
          setSelectedThemeComponent(""); // Reset theme-specific selection
          setSelectedObjectComponent(""); // Reset object-specific selection
          setSelectedRadius(MAP_CONSTANTS.DEFAULT_RADIUS);
          setSelectionType("themes"); // Reset to themes after placing a marker
        })
        .catch((err) => {
          console.error("Failed to save location:", err);
          // Don't reset the form if API call fails so user can try again
        });
    }
  };

  // Handle delete
  const handleDelete = () => {
    setShowConfirmDialog(true);
  };

  // Confirm delete
  const confirmDelete = () => {
    if (selectedIndex !== null && selectedIndex >= 0) {
      const locationToDelete = selectedLocations[selectedIndex];

      if (!locationToDelete || !locationToDelete._id) {
        console.error("Cannot delete: Invalid location or missing ID");
        enqueueSnackbar(t("mapWithComponents.cannotDeleteItem"), {
          variant: "error",
        });
        setShowConfirmDialog(false);
        return;
      }

      // Call API to delete the geolocalised component
      myGeolocalisedServices
        .deleteGeolocalisedComponent(locationToDelete._id)
        .then(() => {
          // On successful deletion from API, update UI
          // Remove marker from map
          markersRef.current[selectedIndex].remove();

          // Remove circle if it exists
          if (circlesRef.current[selectedIndex]) {
            removeCircle(mapRef.current, circlesRef.current[selectedIndex]);
          }

          // Update arrays
          const newLocations = [...selectedLocations];
          newLocations.splice(selectedIndex, 1);
          setSelectedLocations(newLocations);

          // Update indices on all remaining markers
          markersRef.current.forEach((marker, idx) => {
            if (idx >= selectedIndex) {
              const markerEl = marker.getElement();
              const currentIndex = parseInt(markerEl.dataset.locationIndex, 10);
              if (currentIndex > selectedIndex) {
                // Update index
                const newIndex = currentIndex - 1;
                markerEl.dataset.locationIndex = newIndex;
              }
            }
          });

          // Clean up arrays
          circlesRef.current.splice(selectedIndex, 1);
          markersRef.current.splice(selectedIndex, 1);

          // Reset state
          setShowConfirmDialog(false);
          setSelectedLocation(null);
          setSelectedComponent("");
          setSelectedRadius(MAP_CONSTANTS.DEFAULT_RADIUS);
          setIsEditMode(false);
          setSelectedIndex(null);

          enqueueSnackbar(t("mapWithComponents.markerDeleteSuccess"), {
            variant: "success",
          });
        })
        .catch((error) => {
          console.error("Error deleting location from API:", error);
          enqueueSnackbar(t("mapWithComponents.markerDeleteFail"), {
            variant: "error",
          });
          setShowConfirmDialog(false);
        });
    } else {
      setShowConfirmDialog(false);
    }
  };

  useEffect(() => {
    // Improved map initialization with better movement detection
    const initializeMap = () => {
      const map = new maplibregl.Map({
        container: document.getElementById("mapContainer"), // Changed from "mapContainer" string to get the element by ID
        style:
          // "https://api.maptiler.com/maps/streets/style.json?key=HUEOq540tid7txv6xMQT",
          "https://api.maptiler.com/maps/hybrid/style.json?key=1xiFM2Z0YcdYA1H1lhdB",
        center: [1.4442, 43.6047],
        zoom: 11,
      });

      mapRef.current = map;

      // Track longpress variables
      let pressTimer = null;
      let pressStartPoint = null;
      let pressCoordinates = null;
      let hasMoved = false;

      // Movement threshold in pixels - if user moves more than this, it's not a longpress
      const moveThreshold = 10;

      // Handle mouse events for desktop
      map.on("mousedown", (e) => {
        // Record initial position (both in screen coordinates and map coordinates)
        pressStartPoint = e.point; // Screen coordinates (pixels)
        pressCoordinates = [e.lngLat.lng, e.lngLat.lat]; // Map coordinates
        hasMoved = false;

        pressTimer = setTimeout(() => {
          // Only trigger longpress if user hasn't moved significantly
          if (!hasMoved) {
            console.log("Longpress detected at:", pressCoordinates);
            handleLongPress(pressCoordinates);
          }
        }, MAP_CONSTANTS.LONGPRESS_DURATION);
      });

      map.on("mousemove", (e) => {
        if (pressStartPoint) {
          // Calculate distance moved in pixels
          const dx = e.point.x - pressStartPoint.x;
          const dy = e.point.y - pressStartPoint.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // If moved beyond threshold, cancel longpress and mark as moved
          if (distance > moveThreshold) {
            hasMoved = true;
            clearTimeout(pressTimer);
            pressTimer = null;
          }
        }
      });

      map.on("mouseup", () => {
        clearTimeout(pressTimer);
        pressTimer = null;
        pressStartPoint = null;
      });

      // Handle touch events for mobile devices with similar improvements
      map.on("touchstart", (e) => {
        if (e.points && e.points.length > 0) {
          // Record both pixel and geographic coordinates
          pressStartPoint = e.points[0]; // Screen coordinates
          const point = map.unproject(e.points[0]);
          pressCoordinates = [point.lng, point.lat]; // Map coordinates
          hasMoved = false;

          pressTimer = setTimeout(() => {
            if (!hasMoved) {
              console.log("Longpress detected at:", pressCoordinates);
              handleLongPress(pressCoordinates);
            }
          }, MAP_CONSTANTS.LONGPRESS_DURATION);
        }
      });

      map.on("touchmove", (e) => {
        if (pressStartPoint && e.points && e.points.length > 0) {
          // Calculate distance moved in pixels
          const dx = e.points[0].x - pressStartPoint.x;
          const dy = e.points[0].y - pressStartPoint.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // If moved beyond threshold, cancel longpress
          if (distance > moveThreshold) {
            hasMoved = true;
            clearTimeout(pressTimer);
            pressTimer = null;
          }
        }
      });

      map.on("touchend", () => {
        clearTimeout(pressTimer);
        pressTimer = null;
        pressStartPoint = null;
      });

      // Explicitly handle map drag events
      map.on("dragstart", () => {
        hasMoved = true;
        clearTimeout(pressTimer);
        pressTimer = null;
        pressStartPoint = null;
      });

      // Extract the long press handler to a separate function
      const handleLongPress = (coordinates) => {
        console.log("Longpress detected at:", coordinates);

        // Clear any existing temporary markers and circles
        if (currentMarkerRef.current) {
          currentMarkerRef.current.remove();
        }
        if (currentCircleRef.current) {
          removeCircle(map, currentCircleRef.current);
        }

        // Create temporary circle for themes first (so it appears below the marker)
        if (selectionType === "themes") {
          const circleId = `temp-circle-${Date.now()}`;
          updateCircle(
            map,
            circleId,
            coordinates,
            MAP_CONSTANTS.DEFAULT_RADIUS
          );
          currentCircleRef.current = circleId;
        }

        // Add a temporary marker at the pressed location
        const marker = new maplibregl.Marker({
          color: "#d3af5f",
          scale: 1.2,
          anchor: "center",
        })
          .setLngLat(coordinates)
          .addTo(map);

        // Store reference
        currentMarkerRef.current = marker;

        // Perform reverse geocoding to get location name
        fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coordinates[1]}&lon=${coordinates[0]}`
        )
          .then((response) => response.json())
          .then((data) => {
            // Set selected location with geocoding result
            const objValues = Object.values(data.address);
            const finalAdress = {};
            Object.keys(data.address).forEach((key, index) => {
              if (
                MAP_CONSTANTS.REVERSE_GEOCODING_AUTHORIZED_KEYS.includes(key)
              ) {
                finalAdress[key] = objValues[index];
              }
            });

            setSelectedLocation({
              address: finalAdress,
              coordinates: coordinates,
            });

            // Reset to creation mode
            setIsEditMode(false);
            setSelectedIndex(null);
            setSelectedComponent("");
            setSelectedRadius(MAP_CONSTANTS.DEFAULT_RADIUS);
          })
          .catch((error) => {
            console.error("Error with reverse geocoding:", error);

            // Fallback if geocoding fails
            setSelectedLocation({
              address: {
                location: `Location (${coordinates[0].toFixed(
                  4
                )}, ${coordinates[1].toFixed(4)})`,
              },
              coordinates: coordinates,
            });

            // Reset to creation mode
            setIsEditMode(false);
            setSelectedIndex(null);
            setSelectedComponent("");
            setSelectedRadius(MAP_CONSTANTS.DEFAULT_RADIUS);
          });
      };

      // Add geocoder control
      map.addControl(geocoder, "top-left");

      // Add our custom icon after adding the geocoder
      addCustomIconToGeocoder(t, setOpenDrawerMenu);

      geocoder.on("result", (e) => {
        map.flyTo({
          center: e.result.center,
          zoom: 15,
          essential: true,
          curve: 1.2,
        });
      });

      // Wait for map to load before adding markers
      map.on("load", () => {
        // Update selectedLocations with geolocalised data
        const formattedLocations = geolocalisedComponents
          .map((item) => {
            // Extract coordinates from the correct location
            const longitude = item.position.coordinates[0];
            const latitude = item.position.coordinates[1];

            // Determine type based on radius existence
            const type = item.position.radius ? "themes" : "objects";

            // Check if component exists in myComponents before including it
            const componentExists = myComponents[type]?.some((cmp) => {
              return cmp._id === item.objectId;
            });

            if (!componentExists) {
              console.warn(
                `Component not found for ${type} with ID ${item._id}`
              );
              return null; // Return null for filtering later
            }

            return {
              address: item.address || {
                location: `Location (${longitude}, ${latitude})`,
              },
              latitude: latitude,
              longitude: longitude,
              component: item.objectId,
              radius: item.position.radius || 0, // Use 0 for objects
              type: type,
              previewUrl: item.previewUrl, // Store the preview URL,
              _id: item._id,
            };
          })
          .filter(Boolean); // Filter out null values (components that weren't found)

        setSelectedLocations(formattedLocations);

        // Process each geolocalised component
        formattedLocations.forEach((location, index) => {
          // Create marker element with component image
          const markerElement = createMarkerElement(
            location.component,
            location.type,
            myComponents
          );

          // Store location data in marker
          markerElement.dataset.locationIndex = index;
          markerElement.dataset.locationData = JSON.stringify(location);

          // Create the marker
          const marker = new maplibregl.Marker({
            element: markerElement,
            draggable: false,
            anchor: "center",
          })
            .setLngLat([location.longitude, location.latitude])
            .addTo(map);

          // Add click handler to the marker
          markerElement.onclick = function (e) {
            handleCancel();
            e.stopPropagation();
            try {
              const locationData = JSON.parse(this.dataset.locationData);
              const storedIndex = parseInt(this.dataset.locationIndex, 10);
              console.log(
                "Marker clicked with data:",
                locationData,
                "index:",
                storedIndex
              );

              handleMarkerClick(locationData, storedIndex);
            } catch (err) {
              console.error("Error accessing location data", err);
            }
          };

          // Store marker reference
          markersRef.current.push(marker);

          // Create circle for themes
          if (location.type === "themes" && location.radius) {
            const circleId = `circle-${location.component}-${index}`;
            updateCircle(
              map,
              circleId,
              [location.longitude, location.latitude],
              location.radius
            );
            circlesRef.current.push(circleId);
          } else {
            // Push null for objects to maintain index alignment
            circlesRef.current.push(null);
          }
        });

        // Fit map bounds to show all markers if there are any
        if (formattedLocations.length > 0) {
          const bounds = new maplibregl.LngLatBounds();
          formattedLocations.forEach((location) => {
            bounds.extend([location.longitude, location.latitude]);
          });

          map.fitBounds(bounds, {
            padding: 100, // Add some padding around markers
            maxZoom: 15, // Don't zoom in too far
          });
        }
      });
    };
    if (geolocalisedComponents) initializeMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, t, geolocalisedComponents]);

  const handleSelectedComponent = (event) => {
    const value = event.target.value;
    setSelectedComponent(value);

    // Also update the type-specific state
    if (selectionType === "themes") {
      setSelectedThemeComponent(value);
    } else if (selectionType === "objects") {
      setSelectedObjectComponent(value);
    }
  };

  const handleSelectionTypeChange = (event) => {
    const newType = event.target.value;
    const prevType = selectionType;

    // Save current selection to the appropriate variable
    if (prevType === "themes" && selectedComponent) {
      setSelectedThemeComponent(selectedComponent);
    } else if (prevType === "objects" && selectedComponent) {
      setSelectedObjectComponent(selectedComponent);
    }

    // Update selection type
    setSelectionType(newType);

    // Restore the saved selection for the new type
    if (newType === "themes") {
      setSelectedComponent(selectedThemeComponent);
    } else if (newType === "objects") {
      setSelectedComponent(selectedObjectComponent);
    }

    // Handle circle visibility based on selection type
    if (newType === "objects") {
      // Remove circle when switching to objects
      if (currentCircleRef.current) {
        removeCircle(mapRef.current, currentCircleRef.current);
        currentCircleRef.current = null;
      }

      // If in edit mode, also hide the permanent circle
      if (isEditMode && selectedIndex !== null) {
        const circleId = circlesRef.current[selectedIndex];
        if (circleId) {
          // Just hide it by setting opacity to 0 instead of removing it completely
          const map = mapRef.current;
          if (map) {
            map.setPaintProperty(`${circleId}-fill`, "fill-opacity", 0);
            map.setPaintProperty(`${circleId}-border`, "line-opacity", 0);
          }
        }
      }
    } else {
      // Add circle when switching to themes
      if (selectedLocation) {
        // Create temporary circle for new location
        if (!isEditMode && selectedLocation?.coordinates) {
          const circleId = `temp-circle-${Date.now()}`;
          updateCircle(
            mapRef.current,
            circleId,
            selectedLocation.coordinates,
            selectedRadius
          );
          currentCircleRef.current = circleId;
        }

        // If in edit mode, also show the permanent circle
        if (isEditMode && selectedIndex !== null) {
          const circleId = circlesRef.current[selectedIndex];
          if (circleId) {
            const map = mapRef.current;
            if (map) {
              map.setPaintProperty(`${circleId}-fill`, "fill-opacity", 0.2);
              map.setPaintProperty(`${circleId}-border`, "line-opacity", 0.8);
            }
          }
        }
      }
    }
  };

  // Effect to update circle when radius changes in edit mode
  useEffect(() => {
    if (isEditMode && selectedIndex !== null && selectedLocation) {
      const circleId = circlesRef.current[selectedIndex];
      if (circleId) {
        updateCircle(
          mapRef.current,
          circleId,
          selectedLocation.coordinates,
          selectedRadius
        );
      }
    }
  }, [selectedRadius, isEditMode, selectedIndex, selectedLocation]);

  const handleUpdate = () => {
    if (
      isEditMode &&
      selectedIndex !== null &&
      selectedLocation &&
      selectedComponent
    ) {
      // Create updated location
      const updatedLocation = {
        address: selectedLocation.address,
        latitude: selectedLocation.coordinates[1],
        longitude: selectedLocation.coordinates[0],
        component: selectedComponent,
        radius: selectionType === "themes" ? selectedRadius : 0,
        type: selectionType,
        objectId: selectedLocations.objectId,
        _id: selectedLocations[selectedIndex]._id, // Keep the original ID for API update
      };

      // Save to API first
      saveLocationToAPI(updatedLocation, true)
        .then(() => {
          // Update in locations array
          const newLocations = [...selectedLocations];
          newLocations[selectedIndex] = updatedLocation;
          setSelectedLocations(newLocations);

          // Handle circle based on type
          const circleId = circlesRef.current[selectedIndex];
          if (selectionType === "themes") {
            // Show/update circle for themes
            if (circleId) {
              // Update existing circle
              updateCircle(
                mapRef.current,
                circleId,
                selectedLocation.coordinates,
                selectedRadius
              );
              // Make sure it's visible
              const map = mapRef.current;
              if (map) {
                map.setPaintProperty(`${circleId}-fill`, "fill-opacity", 0.2);
                map.setPaintProperty(`${circleId}-border`, "line-opacity", 0.8);
              }
            } else {
              // Create new circle if it doesn't exist
              const newCircleId = `circle-${Date.now()}`;
              updateCircle(
                mapRef.current,
                newCircleId,
                selectedLocation.coordinates,
                selectedRadius
              );
              circlesRef.current[selectedIndex] = newCircleId;
            }
          } else {
            // Hide circle for objects
            if (circleId) {
              const map = mapRef.current;
              if (map) {
                map.setPaintProperty(`${circleId}-fill`, "fill-opacity", 0);
                map.setPaintProperty(`${circleId}-border`, "line-opacity", 0);
              }
            }
          }

          // Update marker element
          const markerElement = markersRef.current[selectedIndex].getElement();
          markerElement.dataset.locationData = JSON.stringify(updatedLocation);

          // Reset states
          setSelectedLocation(null);
          setSelectedComponent("");
          setSelectedThemeComponent(""); // Reset theme-specific selection
          setSelectedObjectComponent(""); // Reset object-specific selection
          setSelectedRadius(MAP_CONSTANTS.DEFAULT_RADIUS);
          setIsEditMode(false);
          setSelectedIndex(null);
        })
        .catch((err) => {
          console.error("Failed to update location:", err);
          // Don't reset the form if API call fails so user can try again
        });
    }
  };

  // Shared function to handle marker clicks
  const handleMarkerClick = (locationData, storedIndex) => {
    // Clear any existing temporary markers and circles
    if (currentMarkerRef.current) {
      currentMarkerRef.current.remove();
    }
    if (currentCircleRef.current) {
      removeCircle(mapRef.current, currentCircleRef.current);
    }

    // Find the component to get its preview URL
    const component = myComponents[locationData.type].find(
      (cmp) => cmp._id === locationData.component
    );
    const previewUrl = component?.previewUrl || locationData.previewUrl;
    const radius = locationData.radius || MAP_CONSTANTS.DEFAULT_RADIUS;

    // Set selected location and component with previewUrl
    setSelectedLocation({
      address: locationData.address,
      coordinates: [locationData.longitude, locationData.latitude],
      previewUrl: previewUrl, // Include preview URL
    });

    setSelectedComponent(locationData.component);
    // Save both the selected radius and the original radius
    setSelectedRadius(radius);
    setOriginalRadius(radius); // Store original radius
    setIsEditMode(true);
    setSelectedIndex(storedIndex);

    // Set selection type based on locationData.type
    setSelectionType(locationData.type);

    // Show circle if it's a theme
    if (locationData.type === "themes" && circlesRef.current[storedIndex]) {
      const circleId = circlesRef.current[storedIndex];
      const map = mapRef.current;
      if (map) {
        map.setPaintProperty(`${circleId}-fill`, "fill-opacity", 0.2);
        map.setPaintProperty(`${circleId}-border`, "line-opacity", 0.8);
      }
    }
  };

  return (
    <>
      <DrawerMenu
        open={openDrawerMenu}
        setOpen={() => setOpenDrawerMenu(false)}
      />
      {selectedLocation && (
        <LocationDetailsCard
          selectedLocation={selectedLocation}
          selectedComponent={selectedComponent}
          selectionType={selectionType}
          selectedRadius={selectedRadius}
          isEditMode={isEditMode}
          myComponents={myComponents}
          onCancel={handleCancel}
          onComponentChange={handleSelectedComponent}
          onSelectionTypeChange={handleSelectionTypeChange}
          onRadiusChange={handleRadiusChange}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
          onAccept={handleAccept}
          t={t}
        />
      )}
      <ConfirmDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={confirmDelete}
        t={t}
      />
      <MapContainer id="mapContainer"></MapContainer>
    </>
  );
};

export default ComponentsMapManagement;
